import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardFooter } from '@/components/card/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/dialog/dialog'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'

import { createApplicantMutation } from '@/lib/queries/business/v1/applicant/mutations'
import { searchApplicantQueryKey } from '@/lib/queries/business/v2/applicant/search/queries'
import { cn } from '@/lib/utils'

import { useRegisterApplicantDialog } from './hooks/use-register-applicant-dialog'

const formSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional(),
  tags: z
    .string()
    .optional()
    .refine(
      (tags) => {
        const split = tags?.split(/\s*,\s*/)
        const validTags = split?.filter((x) => x.trim().length > 0) ?? []
        return validTags.length <= 5
      },
      {
        message: 'You can only add up to 5 tags.',
      }
    ),
})

export const RegisterApplicantDialog = () => {
  const { isDialogOpen, closeDialog } = useRegisterApplicantDialog()

  return (
    <Dialog
      open={isDialogOpen}
      onOpenChange={(open) => {
        if (!open) {
          void closeDialog()
        }
      }}
    >
      <DialogContent
        onPointerDownOutside={(e) => {
          e.preventDefault()
        }}
      >
        <DialogHeader className="mb-5">
          <DialogTitle>Add New Applicant</DialogTitle>
        </DialogHeader>

        <NewApplicant />
      </DialogContent>
    </Dialog>
  )
}

const NewApplicant = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
  })
  const mutationCreateApplicant = useMutation(createApplicantMutation)

  const handleSubmit = form.handleSubmit((data) => {
    const tagsSplit = data.tags?.split(/\s*,\s*/) ?? []
    const tagsTrimmed = tagsSplit.map((x) => x.trim())
    const tagsFiltered = tagsTrimmed.filter((x) => x.length > 0)
    const tags = [...new Set(tagsFiltered)]

    mutationCreateApplicant.mutate(
      {
        name: data.name,
        email: data.email,
        phone: data.phone,
        tags,
      },
      {
        onSuccess: async (newApplicant) => {
          await queryClient.invalidateQueries({
            queryKey: searchApplicantQueryKey.base,
          })
          toast.success('Applicant added successfully.')
          await navigate({ to: `/applicants/${newApplicant.path_id}` })
          form.reset()
        },
        onError: () => {
          toast.error('Unable to add the applicant. Please try again later.')
        },
      }
    )
  })

  const { errors } = form.formState

  return (
    <Card>
      <Form {...form}>
        <form>
          <CardContent className="p-6">
            <div className="mb-2 text-sm text-muted-foreground">Please enter the applicant information.</div>
            <div className="space-y-3">
              <FormField
                control={form.control}
                rules={{ required: true }}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name*</FormLabel>
                    <FormControl>
                      <Input className="placeholder:text-faint-foreground" placeholder="Pietro Schirano" {...field} />
                    </FormControl>
                    <FormDescription>
                      You can add applicants with the same name. Please differentiate as needed.
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                rules={{ required: true }}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address*</FormLabel>
                    <FormControl>
                      <Input className="placeholder:text-faint-foreground" placeholder="email@example.com" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                rules={{ required: true }}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input className="placeholder:text-faint-foreground" placeholder="123 456 7890" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        className="placeholder:text-faint-foreground"
                        placeholder="e.g. Workers, Refugees, Asylum Seeker"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className={cn(!!errors.tags?.message && 'text-red-500')}>
                      {errors.tags?.message ?? 'Assign one or more tags to the applicant, separated by commas.'}
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>

          {/* SUBMIT */}
          <CardFooter>
            <Button disabled={mutationCreateApplicant.isPending} onClick={handleSubmit}>
              Save
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  )
}
